import React from "react";
import Typography from "@material-ui/core/Typography/Typography";
import { Card, CardContent, Grid, Paper, WithStyles, withStyles } from "@material-ui/core";
import { NavLink } from "react-router-dom";
import { styles } from "../CardsView.styles";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined"; //for Manage Orders
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined"; //for Manage subscriptions
import EmojiPeopleIcon from "@material-ui/icons/EmojiPeople"; // Manage subscription requests
import AssignmentIcon from "@material-ui/icons/Assignment"; // Manage requests/reports
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined"; //for Manage users
import PollOutlinedIcon from "@material-ui/icons/PollOutlined"; //for Stats
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted"; // for Tasks
import TrendingUpIcon from "@material-ui/icons/TrendingUp"; // Use cases
import ForumIcon from "@material-ui/icons/Forum"; // contracts
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline"; // business partners
import QuestionAnswerIcon from "@material-ui/icons/QuestionAnswer"; // manage contracts
import MoneyIcon from "@material-ui/icons/Money";
import StoreIcon from "@material-ui/icons/Store";
import { useTranslation } from "react-i18next";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { PageBanner } from "../../components/PageBanner/PageBanner";
import { isPermissionGranted } from "../../../utilities/permissionUtils";
import { useGetRoles } from "../../../hooks/useGetRole";
import { Permission } from "../../../constants/PERMISSIONS";
import configuration from "../../../utilities/configuration";

export interface IAdminViewProps extends WithStyles<typeof styles> {}

const AdminViewPure: React.FunctionComponent<IAdminViewProps> = ({ classes }) => {
    const { t } = useTranslation();

    interface ICard {
        icon: JSX.Element;
        title: string;
        path: string;
        scope?: Permission | Permission[];
    }

    interface ISection {
        name: string;
        cards: ICard[];
    }

    const adminPanelCards: ICard[] = [
        {
            icon: <ListAltOutlinedIcon />,
            title: t("admin.manageOrdersView.manageOrders"),
            path: "/admin/manage-orders",
            scope: ["orders.read", "orders.write"]
        },
        {
            icon: <DateRangeOutlinedIcon />,
            title: t("admin.manageSubscriptionsView.manageSubscriptions"),
            path: "/admin/manage-subscriptions",
            scope: "subscriptions.read"
        },
        {
            icon: <EmojiPeopleIcon />,
            title: "Manage subscription requests",
            path: "/admin/manage-subscription-requests",
            scope: "subscriptions.read"
        },
        {
            icon: <AssignmentIcon />,
            title: "Manage requests/reports",
            path: "/admin/manage-reports",
            scope: ["reports.read", "reports.write"]
        },
        {
            icon: <GroupOutlinedIcon />,
            title: t("admin.manageUsersView.manageUsers"),
            path: "/admin/manage-users",
            scope: ["users.read", "users.write"]
        },
        {
            icon: <StoreIcon />,
            title: "Manage products",
            path: "/admin/manage-products",
            scope: ["products.read", "products.write"]
        },
        {
            icon: <TrendingUpIcon />,
            title: "Manage use cases",
            path: "/admin/manage-use-cases",
            scope: ["usecases.read", "usecases.write"]
        },
        {
            icon: <PollOutlinedIcon />,
            title: t("admin.statisticsView.statistics"),
            path: "/admin/stats",
            scope: ["stats.read"]
        },
        {
            icon: <MoneyIcon />,
            title: "Quotas",
            path: "/admin/quotas",
            scope: ["orders.quota.read", "orders.quota.write"]
        },
        {
            icon: <PeopleOutlineIcon />,
            title: "Business partners",
            path: "/admin/business-partners",
            scope: ["businessPartners.read", "businessPartners.write"]
        },
        // tasks only shown for DEV for now
        ...(configuration.envName === "Development"
            ? [
                  {
                      icon: <FormatListBulletedIcon />,
                      title: t("admin.taskView.tasks"),
                      path: "/admin/tasks",
                      scope: ["tasks.read", "tasks.write"]
                  } as ICard
              ]
            : [])
    ];

    const contractAdminCards: ICard[] = [
        {
            icon: <ForumIcon />,
            title: "Contract meta data",
            path: "/admin/contracts",
            scope: ["contracts.read", "contracts.write"]
        },
        {
            icon: <QuestionAnswerIcon />,
            title: "Manage Contracts/OF's",
            path: "/admin/contracts/contracts",
            scope: ["contracts.read", "contracts.write"]
        }
    ];
    const salesPanelCards: ICard[] = [
        {
            icon: <PeopleOutlineIcon />,
            title: "User registration & Trial subscription entitlement for sales",
            path: "/admin/sales-registration",
            scope: ["users.sales.register"]
        }
    ];

    const sections: ISection[] = [
        {
            name: "Administration panel",
            cards: adminPanelCards
        },
        {
            name: "Contract Administration",
            cards: contractAdminCards
        },
        {
            name: "Sales panel",
            cards: salesPanelCards
        }
    ];

    const roles = useGetRoles();

    return (
        <>
            <PageBanner title="Administration panel" isPublic={false} />
            <CenteredContainer>
                {sections.map(section => {
                    const cards = section.cards.filter(card => !card.scope || isPermissionGranted(roles, card.scope));
                    if (cards.length === 0) return null;
                    return (
                        <Paper elevation={0} className={classes.box} key={section.name}>
                            <div className={classes.title}>
                                <Typography variant="h3" className={classes.header}>
                                    {section.name}
                                </Typography>
                            </div>
                            <Grid container spacing={3} direction="row" alignItems="center">
                                {cards.map((card, i) => (
                                    <Grid item xs={12} sm={4} key={i}>
                                        <NavLink to={card.path} style={{ textDecoration: "none" }}>
                                            <Card className={classes.card}>
                                                <CardContent className={classes.cardContent}>
                                                    {card.icon}
                                                    <span className={classes.cardTitle}>{card.title}</span>
                                                </CardContent>
                                            </Card>
                                        </NavLink>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    );
                })}
            </CenteredContainer>
        </>
    );
};

export const AdminView = withStyles(styles)(AdminViewPure);
